export const CONSUMPTION_STAGES = ['collection', 'comments', 'enrichment', 'analysis'] as const;
export type ConsumptionStage = typeof CONSUMPTION_STAGES[number];
export interface MarketConsumptionStage {
    stage: ConsumptionStage;
    accounting: 'shared_collection_budget' | 'included_in_collection' | 'separate_unquoted';
    max_records: number | null;
    initial_jobs_upper_bound: number | null;
    estimated_cost_micros: null;
    cost_currency: null;
}
export interface MarketConsumptionPreview {
    contract_version: 'market-consumption-preview/v1';
    basis: 'compiled_initial_plan';
    record_unit: 'unique_canonical_records';
    allocated_records: number;
    stages: MarketConsumptionStage[];
}
const record = (value: unknown): value is Record<string, unknown> =>
    value !== null && typeof value === 'object' && !Array.isArray(value);
const count = (value: unknown): value is number => Number.isSafeInteger(value) && Number(value) >= 0;

/** v1 carries allocation ceilings only. Unknown prices are not zero, and enrichment is not additive. */
export function validConsumptionPreview(value: unknown, maxRecords: number,
    initialJobs?: number | null, allocations?: unknown): value is MarketConsumptionPreview {
    if (!record(value) || value.contract_version !== 'market-consumption-preview/v1'
        || value.basis !== 'compiled_initial_plan' || value.record_unit !== 'unique_canonical_records'
        || !count(value.allocated_records) || value.allocated_records !== maxRecords
        || !Array.isArray(value.stages) || value.stages.length !== CONSUMPTION_STAGES.length) return false;
    const accounting = ['shared_collection_budget', 'shared_collection_budget', 'included_in_collection', 'separate_unquoted'];
    if (!value.stages.every((stage, index) => record(stage) && stage.stage === CONSUMPTION_STAGES[index]
        && stage.accounting === accounting[index]
        && (index === 3 ? stage.max_records === null && stage.initial_jobs_upper_bound === null
            : count(stage.max_records) && (stage.initial_jobs_upper_bound === null || count(stage.initial_jobs_upper_bound)))
        && stage.estimated_cost_micros === null && stage.cost_currency === null)) return false;
    const [collection, comments, enrichment] = value.stages as MarketConsumptionStage[];
    if (initialJobs !== undefined) {
        const expectedJobs = collection.initial_jobs_upper_bound === null || comments.initial_jobs_upper_bound === null
            ? null : collection.initial_jobs_upper_bound + comments.initial_jobs_upper_bound;
        if (initialJobs !== expectedJobs) return false;
    }
    if (allocations !== undefined) {
        if (!Array.isArray(allocations) || !allocations.every(row => record(row)
            && count(row.max_records) && count(row.comment_records) && row.comment_records <= row.max_records)) return false;
        if (allocations.reduce((sum, row) => sum + row.max_records, 0) !== value.allocated_records
            || allocations.reduce((sum, row) => sum + row.comment_records, 0) !== comments.max_records) return false;
    }
    return collection.max_records! + comments.max_records! === value.allocated_records
        && enrichment.max_records! <= collection.max_records!
        && enrichment.initial_jobs_upper_bound === 0;
}
